import { useState, useEffect } from "react";
import {
  readListView,
  saveListView,
  rangeToSaved,
  rangeFromSaved,
  sortFromSaved,
  pageFromSaved,
  rowsPerPageFromSaved,
} from "../utils/listView";
import {
  GetTP,
  GetAllOnlinePolicies,
  GetOnlinePolicy,
  RetryOnlinePolicy,
  HoldOnlinePolicy,
} from "../../../../Service/_index";
import {
  ToastError,
  ToastSuccess,
} from "../../../../UiComponents/Toaster/Toast";
import moment from "moment";
import dayjs from "dayjs";
import { vehicleData } from "../OptionsData";
import { parseMongoDate } from "../utils/dateHelpers";

/**
 * Normalise a date from whichever library produced it.
 *
 * The range picker hands back dayjs objects, the defaults are dayjs, and a
 * value restored from elsewhere could be a plain Date or an ISO string. dayjs
 * and moment both expose toDate(); everything else goes through the Date
 * constructor.
 */
const toJsDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Custom hook for managing online policy data fetching and state
 */
export const useOnlinePolicyData = () => {
  const [openLoader, setOpenLoader] = useState(false);
  const [tpData, setTpData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [filteredVehicleData, setFilteredVehicleData] = useState(vehicleData);
  // What the list showed before the user opened a policy's History or Edit (utils/listView.js), read once.
  const [saved] = useState(readListView);
  const [searchQuery, setSearchQuery] = useState(() => (typeof saved.search === "string" ? saved.search : ""));

  // Pagination state (Material-UI uses 0-indexed pages)
  const [page, setPage] = useState(() => pageFromSaved(saved.page));
  const [rowsPerPage, setRowsPerPage] = useState(() => rowsPerPageFromSaved(saved.rowsPerPage));
  const [totalCount, setTotalCount] = useState(0);

  /**
   * The default date range: the current calendar month.
   *
   * The list is opened to look at recent work, and an unbounded query grows
   * without limit as the collection does. Built lazily in a function so a tab
   * left open across midnight on the 1st still gets the right month whenever
   * the filters are reset.
   */
  // dayjs, not Date: FilterBox renders this through antd v5's RangePicker,
  // which only accepts dayjs values and renders an empty box for anything else.
  // The payload below converts back out again, so the API still gets ISO.
  const thisMonthRange = () => [
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ];

  // Filter + sort state. Every one of these is sent to the server — none of it
  // is applied in the browser, so the paging counts stay honest.
  const [companyFilter, setCompanyFilter] = useState(() =>
    typeof saved.company === "string" && saved.company ? saved.company : null
  );
  const [dateRange, setDateRange] = useState(() => rangeFromSaved(saved.dateRange, thisMonthRange()));
  const [sort, setSort] = useState(() =>
    sortFromSaved(saved.sort, { sortBy: "createdAt", sortOrder: "desc" })
  );

  // Kept for the way back from History / Edit.
  useEffect(() => {
    saveListView({
      search: searchQuery,
      page,
      rowsPerPage,
      company: companyFilter,
      dateRange: rangeToSaved(dateRange, thisMonthRange()),
      sort,
    });
  }, [searchQuery, page, rowsPerPage, companyFilter, dateRange, sort]);

  // Load TP data on mount only
  useEffect(() => {
    loadTPData();
  }, []);

  const loadTPData = async () => {
    try {
      const res = await GetTP();
      if (res?.data) {
        setTpData(res.data);
      }
    } catch (error) {
      console.error("Error loading TP data:", error);
    }
  };

  /**
   * @param {object|null} searchPayload  { search } or null
   * @param {number|null} currentPage    0-indexed page, or null for state
   * @param {number|null} currentRowsPerPage
   * @param {object} overrides  { company, dateRange, sort } — pass these when
   *   reloading straight after changing a filter, since the state setter has
   *   not landed yet and reading it here would send the previous value.
   *   { silent: true } — a live-update reload: no loader, no toast, and a
   *   failure keeps the rows already on screen.
   */
  const loadAllOnlinePolicies = async (
    searchPayload = null,
    currentPage = null,
    currentRowsPerPage = null,
    overrides = {}
  ) => {
    const silent = !!overrides.silent;
    try {
      if (!silent) setOpenLoader(true);

      // Get the page number (0-indexed from Material-UI)
      const pageNumber = currentPage !== null ? currentPage : page;
      const limit =
        currentRowsPerPage !== null ? currentRowsPerPage : rowsPerPage;

      // Build the complete payload with pagination and search
      // Convert to 1-indexed for backend (MongoDB expects page 1, 2, 3...)
      // Who sees what is decided by the SERVER from the login headers
      // (backend shared/onlinePolicyListScope.js): a CLIENT sees his own
      // client's policies, the SUPER_ADMIN every client's, everyone else only
      // the ones they created. The userId sent below only narrows further — it
      // cannot widen. No client-side filtering, which would break paging counts.
      const loggedInUserType = sessionStorage.getItem("userType");
      const isMasterAdmin =
        String(loggedInUserType || "").toUpperCase() === "CLIENT";
      const loggedInUserId = sessionStorage.getItem("UserId");

      const effectiveCompany =
        overrides.company !== undefined ? overrides.company : companyFilter;
      const effectiveRange =
        overrides.dateRange !== undefined ? overrides.dateRange : dateRange;
      const effectiveSort = overrides.sort !== undefined ? overrides.sort : sort;

      const payload = {
        page: pageNumber + 1, // Convert 0-indexed to 1-indexed
        limit: limit,
        ...(searchPayload && { search: searchPayload.search }),
        ...(!isMasterAdmin && loggedInUserId
          ? { userId: loggedInUserId }
          : {}),
        ...(effectiveCompany ? { company: effectiveCompany } : {}),
        // Whole days, in the browser's own timezone: picking 1st–31st has to
        // include everything created on the 31st, not stop at its midnight.
        ...(effectiveRange?.[0]
          ? {
            fromDate: moment(toJsDate(effectiveRange[0]))
              .startOf("day")
              .toISOString(),
          }
          : {}),
        ...(effectiveRange?.[1]
          ? {
            toDate: moment(toJsDate(effectiveRange[1]))
              .endOf("day")
              .toISOString(),
          }
          : {}),
        ...(effectiveSort?.sortBy
          ? {
            sortBy: effectiveSort.sortBy,
            sortOrder: effectiveSort.sortOrder || "desc",
          }
          : {}),
      };

      console.log("API Payload (with pagination):", payload);
      console.log("Material-UI page (0-indexed):", pageNumber);
      console.log("Backend page (1-indexed):", pageNumber + 1);

      const response = silent ? await GetAllOnlinePolicies(payload, { silent: true }) : await GetAllOnlinePolicies(payload);

      console.log("API Response:", response);

      if (response && !response.error) {
        // Handle response data structure
        const policies = response.data?.policies || response.data || [];
        const total = response?.totalCount;

        setTableData(policies);
        setTotalCount(total);
        // A page kept from earlier that no longer exists (policies were removed or a filter narrowed it): the last one.
        if (!policies.length && total > 0 && pageNumber > 0) {
          setPage(Math.max(0, Math.ceil(total / limit) - 1));
        }
        console.log("Loaded online policies:", policies);
        console.log("Total count:", total);
      } else if (silent) {
        // a live-update reload that failed: keep what is on screen
      } else {
        console.log("No policies found or error:", response);
        setTableData([]);
        setTotalCount(0);
        if (searchPayload) {
          ToastError("No policies found matching search criteria");
        }
      }
      setOpenLoader(false);
    } catch (error) {
      console.error("Error loading online policies:", error);
      setOpenLoader(false);
      if (silent) return;
      setTableData([]);
      setTotalCount(0);
      ToastError("Failed to load policies from server");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };

  /**
   * Fetch a policy WITHOUT the full-screen loader.
   *
   * Used to check a row is still current before acting on it. The table is
   * rendered once and the automation server keeps working in the background,
   * so a row on screen can be minutes out of date — its Edit button may still
   * be showing for a job that has since started or finished.
   *
   * Returns null rather than toasting on failure: the caller decides whether a
   * failed freshness check should block the action.
   */
  const fetchPolicyByIdSilent = async (policyId) => {
    try {
      const response = await GetOnlinePolicy(policyId, { silent: true });
      if (response && !response.error && response.data) return response.data;
      return null;
    } catch (error) {
      console.error("Silent policy refresh failed:", error);
      return null;
    }
  };

  const fetchPolicyById = async (policyId) => {
    try {
      setOpenLoader(true);
      console.log("Fetching policy for edit with ID:", policyId);

      const response = await GetOnlinePolicy(policyId);

      if (response && !response.error && response.data) {
        const policy = response.data;
        console.log("Fetched policy data:", policy);

        setOpenLoader(false);
        return policy;
      } else {
        console.log("Policy not found in API");
        ToastError("Policy not found");
        setOpenLoader(false);
        return null;
      }
    } catch (error) {
      console.error("Error fetching policy data:", error);
      setOpenLoader(false);
      ToastError("Error loading policy data");
      return null;
    }
  };

  /**
   * Rewrite ONE row of the table in place.
   *
   * The row actions — retry, hold, release — used to finish by calling
   * loadAllOnlinePolicies, which drops the whole table behind a full-width
   * loader and re-fetches every row just to change one status chip. On a slow
   * connection that reads as the page reloading underneath you, and it throws
   * away the scroll position and any open row state with it.
   *
   * Each of those actions only ever changes the job state of the row it acted
   * on, so that is all that is written back. Nothing else on screen moves.
   */
  const patchTableRow = (policyId, patch) => {
    if (!policyId || !patch) return;
    const target = String(policyId);
    setTableData((prev) =>
      prev.map((row) =>
        String(row._id || row.policyId || row.id) === target
          ? { ...row, ...patch }
          : row
      )
    );
  };

  /**
   * The row fields that mirror a job document.
   *
   * The list pipeline flattens the joined job onto the row as jobStatus /
   * attempts / maxAttempts (dao/onlinePolicyDao.js getAllOnlinePolicies), so a
   * job returned by an action has to be flattened the same way to slot in.
   * The hold / retry / release responses carry the summary already
   * (jobStatus, parts, statusLabel, attempts, maxAttempts, lastError); an older
   * server hands back the raw job, whose state is `status`.
   */
  const rowPatchFromJob = (job) =>
    job
      ? {
        jobStatus: job.jobStatus ?? job.status,
        attempts: job.attempts ?? 0,
        maxAttempts: job.maxAttempts ?? 3,
        parts: Array.isArray(job.parts) ? job.parts : [],
        statusLabel: job.statusLabel ?? "",
        lastError: job.lastError ?? null,
        // "Hold" asked for while it ran (applied when the attempt ends)
        holdRequested: !!job.holdRequested,
        // a step action answers with the steps as they are now: the hover card follows
        ...(Array.isArray(job.relianceStages) ? { relianceStageView: job.relianceStages } : {}),
      }
      : null;

  /**
   * Patch a row from what an action returned. When the response has no `parts`
   * (an older server) the row would keep stale ones, so read the policy back
   * instead — one silent request, still no table reload.
   */
  const patchRowFromResponse = async (policyId, job) => {
    if (job && Array.isArray(job.parts)) {
      patchTableRow(policyId, rowPatchFromJob(job));
      return;
    }
    const fresh = await fetchPolicyByIdSilent(policyId);
    if (fresh) {
      // A policy's own `status` is not the job's, so name the job state explicitly.
      patchTableRow(policyId, rowPatchFromJob({ ...fresh, status: fresh.jobStatus }));
    } else {
      patchTableRow(policyId, rowPatchFromJob(job));
    }
  };

  const retryOnlinePolicy = async (policyId) => {
    try {
      // No loader: the button shows its own spinner, and the row is patched
      // from the job the API hands back rather than re-fetched.
      const response = await RetryOnlinePolicy(policyId);
      if (response && !response.error) {
        await patchRowFromResponse(policyId, response.data);
        return true;
      }
      // A refusal usually means the world moved on — show the status it
      // reported so the row's buttons stop offering something impossible.
      if (response?.currentStatus) {
        patchTableRow(policyId, { jobStatus: response.currentStatus });
      }
      return false;
    } catch (error) {
      console.error("Error in hook retryOnlinePolicy:", error);
      return false;
    }
  };

  /**
   * Hold a queued policy so the automation skips it, or release it back.
   *
   * Silent on both paths: on success the row takes the returned job's state, and
   * on the usual failure — the automation claimed the job while the page sat
   * open — it takes the status the server reported, so the buttons stop lying
   * without the table blinking.
   */
  const setPolicyHold = async (policyId, hold) => {
    try {
      const response = await HoldOnlinePolicy(policyId, hold);
      if (response && !response.error) {
        await patchRowFromResponse(policyId, response.data);
        return true;
      }
      if (response?.currentStatus) {
        patchTableRow(policyId, { jobStatus: response.currentStatus });
      }
      return false;
    } catch (error) {
      console.error("Error in hook setPolicyHold:", error);
      return false;
    }
  };

  /** Back to the defaults the page opens on — this month, no company. */
  const resetFilters = () => {
    const range = thisMonthRange();
    setCompanyFilter(null);
    setDateRange(range);
    // The search box is NOT cleared here. It has its own X, and clearing it
    // would fire the debounced-search effect 500ms later — a second query on
    // top of the one below.
    setSort({ sortBy: "createdAt", sortOrder: "desc" });
    setPage(0);
    loadAllOnlinePolicies(null, 0, rowsPerPage, {
      company: null,
      dateRange: range,
      sort: { sortBy: "createdAt", sortOrder: "desc" },
    });
  };

  return {
    openLoader,
    setOpenLoader,
    tpData,
    tableData,
    companyFilter,
    setCompanyFilter,
    dateRange,
    setDateRange,
    sort,
    setSort,
    resetFilters,
    thisMonthRange,
    filteredVehicleData,
    setFilteredVehicleData,
    loadAllOnlinePolicies,
    fetchPolicyById,
    fetchPolicyByIdSilent,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
    handleChangePage,
    handleChangeRowsPerPage,
    retryOnlinePolicy,
    setPolicyHold,
    patchTableRow,
    patchRowFromResponse,
  };
};
